import React, { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Package, ShoppingBag, Users, AlertTriangle, IndianRupee, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";

const KpiCard = ({ label, value, icon: Icon, accent, idx }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: idx * 0.06, ease: [0.22, 1, 0.36, 1] }}
    className="relative overflow-hidden rounded-2xl p-5"
    style={{ background: "linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.015) 100%)", border: "1px solid rgba(255,255,255,0.06)" }}
    data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className="flex items-center justify-between mb-3">
      <span className="font-label text-[11px] uppercase tracking-[0.22em] text-cream/75">{label}</span>
      <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: accent }}>
        <Icon size={15} className="text-cream" />
      </div>
    </div>
    <p className="font-hero text-3xl text-cream">{value}</p>
    <div aria-hidden className="absolute -bottom-16 -right-16 w-40 h-40 rounded-full opacity-20 blur-2xl" style={{ background: accent }} />
  </motion.div>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <p className="font-cormorant text-xl text-cream/75">Loading…</p>;

  return (
    <div data-testid="admin-dashboard">
      <p className="font-label text-[11px] uppercase tracking-[0.22em] text-rose/70 mb-2">Welcome back</p>
      <h1 className="font-hero text-3xl text-cream mb-8">
        Today at <span className="serif-italic" style={{ color: "#E8B8CE" }}>rieave.</span>
      </h1>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        <KpiCard idx={0} label="Revenue" value={formatINR(stats.revenue)} icon={IndianRupee} accent="linear-gradient(135deg, #C9789A, #7A3E5B)" />
        <KpiCard idx={1} label="Orders" value={stats.orders} icon={ShoppingBag} accent="linear-gradient(135deg, #9B8FBF, #524A70)" />
        <KpiCard idx={2} label="Customers" value={stats.customers} icon={Users} accent="linear-gradient(135deg, #E8B8CE, #9B5F82)" />
        <KpiCard idx={3} label="Products" value={stats.products} icon={Package} accent="linear-gradient(135deg, #D4B896, #836745)" />
        <KpiCard idx={4} label="Low stock" value={stats.low_stock} icon={AlertTriangle} accent="linear-gradient(135deg, #C9789A, #D4B896)" />
      </div>

      <div className="rounded-2xl p-6" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={14} className="text-rose" />
          <p className="font-label text-[11px] uppercase tracking-[0.22em] text-cream/75">Recent orders</p>
        </div>
        {stats.recent_orders?.length === 0 ? (
          <p className="text-cream/65">No orders yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-label text-[11px] uppercase tracking-[0.22em] text-cream/65 border-b border-white/10">
                <th className="py-2">Order #</th><th>Customer</th><th>Payment</th><th>Status</th><th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_orders.map(o => (
                <tr key={o.id} className="border-b border-white/10 last:border-b-0 text-cream/80" data-testid={`dashboard-order-${o.order_number}`}>
                  <td className="py-3 font-cormorant text-cream">{o.order_number}</td>
                  <td>{o.customer_email || o.customer_phone || "—"}</td>
                  <td><span className="font-label text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full bg-white/5 text-cream/80">{o.payment_method}</span></td>
                  <td><span className="font-label text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full" style={{ background: "rgba(201,120,154,0.18)", color: "#E8B8CE" }}>{o.order_status}</span></td>
                  <td className="text-right font-body text-cream">{formatINR(o.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
